import { createElement, type ComponentPropsWithoutRef, type JSX } from "react";

import { cn } from "@/lib/utils";

import { MUI_TYPOGRAPHY_VARIANTS, type MuiTypographyVariant } from "./mui-typography-shape";

export type { MuiTypographyVariant };

// MUI's `Typography` under the donor theme, as a shared Atlas component.
//
// The catalog port spelled the theme's scale out as utility strings at every
// call site — the `h1` string verbatim in four files, `subtitle1` as weight 600
// in one and 500 in the next — and the specifications screens compose the same
// component in 68 files (`docs/migration/research/timmy-spec-ui-inventory.md`
// §3). One scale, one home (`docs/agents/migration-guidelines.md`, "Shared
// donor components port as shared Atlas components").
//
// The numbers are the donor theme's (`react-ui/src/theme/theme.ts`) over MUI
// 9.2's defaults (`createTypography.mjs`), fact by fact:
//
//   - every variant is Inter, which is this app's `font-sans` already,
//   - **tracking only where the theme states it** — `h1` −1.5px, `h2` −0.5px,
//     `h4` 0.25px. MUI emits a default `letterSpacing` only under a Roboto
//     family, so every other variant is `normal`, and a Tailwind `tracking-*`
//     on one of them would be ink the source does not have,
//   - the root is `margin: 0` in every variant, and sets **no colour**, so the
//     text inherits `body`'s `text.primary` in the donor. This app's `body`
//     carries shadcn's `foreground` instead, so a caller that wants the
//     donor's ink says `color="primary"` — the colour is never implied.
//
// The element is MUI's `variantMapping` (`Typography.mjs:94-106`): headings to
// their own level, **both subtitles to `h6`**, both bodies to `p`, the rest to
// `span`. A caller whose document outline needs another level passes
// `component` and keeps the variant's look — the details page draws the
// donor's `h4` as its one `h1`.
//
// The scale itself is `mui-typography-shape.ts`'s, beside this file for the
// same reason `chip-shape.ts` sits beside `chip.tsx`: one element that is a
// variant but cannot be a `MuiTypography` needs the classes without the
// component.

type TypographyElement = keyof JSX.IntrinsicElements;

/** MUI's default `variantMapping`. */
const VARIANT_ELEMENT: Record<MuiTypographyVariant, TypographyElement> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  caption: "span",
  overline: "span",
};

/**
 * The two inks the donor passes: `text.primary` and `text.secondary`
 * (`color='text.secondary'`, nine times in the items domain alone).
 */
const COLOR_CLASSES = {
  primary: "text-lennar-ink",
  secondary: "text-lennar-ink-secondary",
} as const;

export interface MuiTypographyProps extends ComponentPropsWithoutRef<"p"> {
  /** MUI's `variant`; `body1` when omitted, as react-ui defaults it. */
  variant?: MuiTypographyVariant;
  /** The element to render — the variant's own when omitted. */
  component?: TypographyElement;
  /**
   * The *text* inks — `"primary"` is `text.primary`, rgba(0,0,0,0.87), and
   * `"secondary"` is `text.secondary`, rgba(0,0,0,0.6) — never the palette's
   * `primary`/`secondary` hues, which no donor Typography passes. Omitted, the
   * text inherits its colour, as MUI's root does.
   */
  color?: keyof typeof COLOR_CLASSES;
  /** `margin-bottom: 0.35em`. */
  gutterBottom?: boolean;
  /** One line, clipped with an ellipsis. */
  noWrap?: boolean;
}

export function MuiTypography({
  variant = "body1",
  component,
  color,
  gutterBottom = false,
  noWrap = false,
  className,
  ...props
}: MuiTypographyProps) {
  return createElement(component ?? VARIANT_ELEMENT[variant], {
    ...props,
    className: cn(
      "m-0",
      MUI_TYPOGRAPHY_VARIANTS[variant],
      color && COLOR_CLASSES[color],
      gutterBottom && "mb-[0.35em]",
      noWrap && "truncate",
      className,
    ),
  });
}
